// 桌面顶导航用户菜单：头像下拉（我的订单/询盘/理赔/消息[未读徽标]/退出登录）
import { useEffect, useState } from 'react';
import { Box, Typography, Menu, MenuItem, ListItemIcon, Divider, Badge } from '@mui/material';
import { ReceiptLong, Forum, GppGood, ChatBubbleOutline, Logout, AccountCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useBuyerAuth } from '@/store/BuyerAuthContext';
import { usePrefs } from '@/store/BuyerPrefsContext';
import { messageApi } from '@/api/messageApi';

export default function UserMenu() {
  const { user, logout } = useBuyerAuth();
  const { t } = usePrefs();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [unread, setUnread] = useState(0);

  const refreshUnread = () => {
    if (!user?.buyerId) return;
    messageApi
      .list(user.buyerId)
      .then((list) => setUnread((list ?? []).filter((m) => !m.isRead).length))
      .catch(() => setUnread(0));
  };

  // 挂载时拉一次；此后每次点开菜单刷新（在消息中心标已读后，回到任意页面点开菜单即可同步）
  useEffect(refreshUnread /* eslint-disable-line react-hooks/exhaustive-deps */, [user?.buyerId]);

  const go = (path: string) => {
    setAnchorEl(null);
    navigate(path);
  };

  const displayName = user?.companyName || user?.email || '';
  const avatarLabel = (displayName || t('profile.avatarFallback')).trim().charAt(0).toUpperCase();

  return (
    <>
      <Box
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          refreshUnread();
        }}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', maxWidth: 200 }}
      >
        <Box
          sx={{
            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg,#10B981,#059669)',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '14px', fontWeight: 700,
          }}
        >
          {avatarLabel}
        </Box>
        <Typography sx={{ fontSize: '13px', color: '#D1FAE5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayName}
        </Typography>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { mt: 1.5, minWidth: 180, borderRadius: '12px', border: '1px solid #F0F2F5', boxShadow: '0 12px 32px rgba(0,0,0,0.12)' } }}
      >
        <MenuItem onClick={() => go('/buyer/profile')} sx={{ py: 1 }}>
          <ListItemIcon><AccountCircle fontSize="small" sx={{ color: '#10B981' }} /></ListItemIcon>
          {t('nav.profile')}
        </MenuItem>
        <MenuItem onClick={() => go('/buyer/orders')} sx={{ py: 1 }}>
          <ListItemIcon><ReceiptLong fontSize="small" sx={{ color: '#10B981' }} /></ListItemIcon>
          {t('nav.myOrders')}
        </MenuItem>
        <MenuItem onClick={() => go('/buyer/inquiries')} sx={{ py: 1 }}>
          <ListItemIcon><Forum fontSize="small" sx={{ color: '#10B981' }} /></ListItemIcon>
          {t('nav.myInquiries')}
        </MenuItem>
        <MenuItem onClick={() => go('/buyer/claims')} sx={{ py: 1 }}>
          <ListItemIcon><GppGood fontSize="small" sx={{ color: '#10B981' }} /></ListItemIcon>
          {t('nav.myClaims')}
        </MenuItem>
        <MenuItem onClick={() => go('/buyer/messages')} sx={{ py: 1 }}>
          <ListItemIcon><ChatBubbleOutline fontSize="small" sx={{ color: '#10B981' }} /></ListItemIcon>
          <Badge badgeContent={unread} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '10px', height: 16, minWidth: 16 } }}>
            {t('nav.messages')}
          </Badge>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            logout();
            navigate('/buyer/login', { replace: true });
          }}
          sx={{ py: 1, color: '#E53935' }}
        >
          <ListItemIcon><Logout fontSize="small" sx={{ color: '#E53935' }} /></ListItemIcon>
          {t('nav.logout')}
        </MenuItem>
      </Menu>
    </>
  );
}
